// src/components/draft/DraftControls.js - Commissioner Controls Component
import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Pause, Play, SkipForward, Undo2, AlertTriangle, PlayCircle, FastForward, RotateCcw } from 'lucide-react';
import { dynastyTheme } from '../../services/colorService';
import { leaguesAPI } from '../../services/apiService';

const DraftControls = ({ socket, draftState, leagueId, year, userIsCommissioner, inline = false, onDraftOrderGenerated }) => {
  const [pauseDialogOpen, setPauseDialogOpen] = useState(false);
  const [undoDialogOpen, setUndoDialogOpen] = useState(false);
  const [startDialogOpen, setStartDialogOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [startError, setStartError] = useState(null);
  const [fillDialogOpen, setFillDialogOpen] = useState(false);
  const [fillProgress, setFillProgress] = useState(null);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetProgress, setResetProgress] = useState(null);
  const [undoError, setUndoError] = useState(null);
  const [undoLoading, setUndoLoading] = useState(false);
  const [endDraftDialogOpen, setEndDraftDialogOpen] = useState(false);
  const [endDraftLoading, setEndDraftLoading] = useState(false);
  const [endDraftError, setEndDraftError] = useState(null);

  // Listen for fill-remainder progress
  useEffect(() => {
    if (!socket) return;
    const handleProgress = (data) => {
      setFillProgress(data);
      if (data.status === 'done') {
        setTimeout(() => setFillProgress(null), 3000);
      }
    };
    socket.on('fill-remainder-progress', handleProgress);
    return () => socket.off('fill-remainder-progress', handleProgress);
  }, [socket]);

  // Listen for reset-draft progress
  useEffect(() => {
    if (!socket) return;
    const handleResetProgress = (data) => {
      setResetProgress(data);
      if (data.status === 'done') {
        setTimeout(() => {
          setResetProgress(null);
          setResetDialogOpen(false);
        }, 2000);
      }
    };
    socket.on('reset-draft-progress', handleResetProgress);
    return () => socket.off('reset-draft-progress', handleResetProgress);
  }, [socket]);

  // Listen for undo success/failure to close dialog properly
  useEffect(() => {
    if (!socket) return;
    const handlePickUndone = () => {
      setUndoLoading(false);
      setUndoError(null);
      setUndoDialogOpen(false);
    };
    const handleError = (data) => {
      // Only capture undo errors when undo dialog is open
      if (undoLoading) {
        setUndoLoading(false);
        setUndoError(data.message || 'Undo failed');
      }
    };
    socket.on('pick-undone', handlePickUndone);
    socket.on('error', handleError);
    return () => {
      socket.off('pick-undone', handlePickUndone);
      socket.off('error', handleError);
    };
  }, [socket, undoLoading]);

  if (!userIsCommissioner) {
    return null; // Only show to commissioner
  }

  // Check if draft has started (status must be in_progress or paused)
  // Note: currentPick exists even in 'waiting' status, so don't check it
  const draftHasStarted = draftState?.status === 'in_progress' ||
                          draftState?.status === 'paused';

  // Start draft
  const handleStartDraft = async () => {
    if (actionLoading) return;

    setActionLoading(true);
    setStartError(null);
    try {
      const response = await leaguesAPI.startLiveDraft(leagueId, year);
      if (response.success) {
        setStartDialogOpen(false);
        // Don't request draft state - server will send it automatically via WebSocket
        // The WebSocket will receive draft-state update automatically
      } else {
        setStartError(response.message || 'Failed to start draft');
      }
    } catch (err) {
      console.error('Failed to start draft:', err);
      setStartError(err.message || 'Failed to start draft');
    } finally {
      setActionLoading(false);
    }
  };

  // Pause draft
  const handlePause = async () => {
    if (!socket || actionLoading) return;

    setActionLoading(true);
    try {
      socket.emit('pause-draft', { league_id: leagueId, year });
      setPauseDialogOpen(false);
    } catch (err) {
      console.error('Failed to pause draft:', err);
    } finally {
      setActionLoading(false);
    }
  };

  // Resume draft
  const handleResume = async () => {
    if (!socket || actionLoading) return;

    setActionLoading(true);
    try {
      socket.emit('resume-draft', { league_id: leagueId, year });
    } catch (err) {
      console.error('Failed to resume draft:', err);
    } finally {
      setActionLoading(false);
    }
  };

  // Skip current pick
  const handleSkip = async () => {
    if (!socket || !draftState?.currentPick || actionLoading) return;

    setActionLoading(true);
    try {
      socket.emit('skip-pick', {
        league_id: leagueId,
        year,
        pick_id: draftState.currentPick.pick_id
      });
    } catch (err) {
      console.error('Failed to skip pick:', err);
    } finally {
      setActionLoading(false);
    }
  };

  // Undo last pick — keep dialog open until server confirms
  const handleUndo = async () => {
    if (!socket || undoLoading) return;

    setUndoLoading(true);
    setUndoError(null);
    socket.emit('undo-pick', { league_id: leagueId, year });
    // Dialog stays open — closed by pick-undone listener or error shown inline
  };

  // Fill Remainder
  const handleFillRemainder = () => {
    if (!socket || fillProgress) return;
    socket.emit('fill-remainder', { league_id: leagueId, year });
    setFillDialogOpen(false);
  };

  // Reset Draft
  const handleResetDraft = () => {
    if (!socket || resetProgress) return;
    socket.emit('reset-draft', { league_id: leagueId, year });
    // Dialog stays open to show progress
  };

  const handleEndDraft = async () => {
    setEndDraftLoading(true);
    setEndDraftError(null);
    try {
      await leaguesAPI.stopLiveDraft(leagueId, year);
      await leaguesAPI.updateLeagueStatus(leagueId, 'finalizing');
      setEndDraftDialogOpen(false);
      window.location.href = `/leagues/${leagueId}`;
    } catch (err) {
      setEndDraftError(err.response?.data?.detail || err.message || 'Failed to end draft');
    } finally {
      setEndDraftLoading(false);
    }
  };

  const isPaused = draftState?.paused || false;
  const hasPicksToUndo = draftState?.currentPickIndex > 0;
  const hasCurrentPick = !!draftState?.currentPick;
  const remainingPicks = draftState?.picks
    ? draftState.picks.filter((p, i) => i >= (draftState.currentPickIndex || 0) && !p.league_player_id).length
    : 0;
  const isFilling = fillProgress && fillProgress.status === 'running';
  const isResetting = resetProgress && resetProgress.status === 'running';
  const picksMade = draftState?.picksMade || 0;

  // Dialogs (shared between inline and card modes) - rendered via portal for proper z-index
  const renderDialogs = () => ReactDOM.createPortal(
    <>
      {/* Pause confirmation dialog */}
      {pauseDialogOpen && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center" style={{ zIndex: 999999 }}>
          <div className={`${dynastyTheme.components.card.base} p-6 max-w-md mx-4`} onClick={(e) => e.stopPropagation()}>
            <h3 className={`${dynastyTheme.components.heading.h3} mb-4`}>
              Pause Draft?
            </h3>
            <p className={`${dynastyTheme.classes.text.white} mb-4`}>
              {draftState?.noTimer
                ? 'This will prevent all picks until you resume the draft.'
                : 'This will stop the timer and prevent all picks until you resume.'
              }
            </p>
            <div className="flex gap-2">
              <button
                onClick={handlePause}
                disabled={actionLoading}
                className={dynastyTheme.components.button.primary}
              >
                {actionLoading ? 'Pausing...' : 'Confirm Pause'}
              </button>
              <button
                onClick={() => setPauseDialogOpen(false)}
                disabled={actionLoading}
                className={dynastyTheme.components.button.secondary}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Undo confirmation dialog */}
      {undoDialogOpen && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center" style={{ zIndex: 999999 }}>
          <div className={`${dynastyTheme.components.card.base} p-6 max-w-md mx-4`} onClick={(e) => e.stopPropagation()}>
            <h3 className={`${dynastyTheme.components.heading.h3} mb-4`}>
              Undo Last Pick?
            </h3>
            <p className={`${dynastyTheme.classes.text.white} mb-4`}>
              This will remove the last pick and return to the previous team's turn. The player will be removed from the roster.
            </p>
            {undoError && (
              <div className="bg-red-900/20 border border-red-500 rounded p-3 mb-4">
                <p className="text-red-400 text-sm">{undoError}</p>
              </div>
            )}
            <div className="flex gap-2">
              <button
                onClick={handleUndo}
                disabled={undoLoading}
                className={dynastyTheme.components.button.primary}
              >
                {undoLoading ? 'Undoing...' : 'Confirm Undo'}
              </button>
              <button
                onClick={() => { setUndoDialogOpen(false); setUndoError(null); setUndoLoading(false); }}
                disabled={undoLoading}
                className={dynastyTheme.components.button.secondary}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Start Draft confirmation dialog */}
      {startDialogOpen && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center" style={{ zIndex: 999999 }}>
          <div className={`${dynastyTheme.components.card.base} p-6 max-w-md mx-4`} onClick={(e) => e.stopPropagation()}>
            <h3 className={`${dynastyTheme.components.heading.h3} mb-4`}>
              Start Live Draft?
            </h3>
            <p className={`${dynastyTheme.classes.text.white} mb-4`}>
              {draftState?.noTimer
                ? 'This will begin the live draft. Picks are untimed — owners pick when ready. All connected users will be notified.'
                : 'This will begin the live draft. The timer will start for the first pick and all connected users will be notified.'
              }
            </p>
            <p className={`${dynastyTheme.classes.text.neutralLight} text-sm mb-4`}>
              Make sure all team owners are ready before starting.
            </p>
            {startError && (
              <div className="bg-red-900/20 border border-red-500 rounded p-3 mb-4">
                <p className="text-red-400 text-sm">{startError}</p>
              </div>
            )}
            <div className="flex gap-2">
              <button
                onClick={handleStartDraft}
                disabled={actionLoading}
                className={`${dynastyTheme.components.button.primary} bg-green-600 hover:bg-green-700`}
              >
                {actionLoading ? 'Starting...' : 'Start Draft'}
              </button>
              <button
                onClick={() => {
                  setStartDialogOpen(false);
                  setStartError(null);
                }}
                disabled={actionLoading}
                className={dynastyTheme.components.button.secondary}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Fill Remainder confirmation dialog */}
      {fillDialogOpen && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center" style={{ zIndex: 999999 }}>
          <div className={`${dynastyTheme.components.card.base} p-6 max-w-md mx-4`} onClick={(e) => e.stopPropagation()}>
            <h3 className={`${dynastyTheme.components.heading.h3} mb-4`}>
              Fill Remaining Picks?
            </h3>
            <p className={`${dynastyTheme.classes.text.white} mb-4`}>
              This will auto-pick all {remainingPicks} remaining picks sequentially. Manual picks will be blocked during this process.
            </p>
            <p className={`text-sm text-red-400 mb-4`}>
              This action cannot be easily undone.
            </p>
            <div className="flex gap-2">
              <button
                onClick={handleFillRemainder}
                className={`${dynastyTheme.components.button.primary} bg-orange-600 hover:bg-orange-700`}
              >
                Fill All Remaining
              </button>
              <button
                onClick={() => setFillDialogOpen(false)}
                className={dynastyTheme.components.button.secondary}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Draft confirmation dialog */}
      {resetDialogOpen && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center" style={{ zIndex: 999999 }}>
          <div className={`${dynastyTheme.components.card.base} p-6 max-w-md mx-4`} onClick={(e) => e.stopPropagation()}>
            <h3 className={`${dynastyTheme.components.heading.h3} mb-4`}>
              Reset Draft?
            </h3>
            {!resetProgress ? (
              <>
                <p className={`${dynastyTheme.classes.text.white} mb-4`}>
                  This will reset the entire draft &mdash; all picks will be undone, all drafted players returned to the free agent pool. This cannot be undone.
                </p>
                <p className={`text-sm text-red-400 mb-4`}>
                  {picksMade} pick{picksMade !== 1 ? 's' : ''} will be reversed and cash refunded.
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={handleResetDraft}
                    className={`${dynastyTheme.components.button.primary} bg-red-600 hover:bg-red-700`}
                  >
                    Reset Entire Draft
                  </button>
                  <button
                    onClick={() => setResetDialogOpen(false)}
                    className={dynastyTheme.components.button.secondary}
                  >
                    Cancel
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className={`${dynastyTheme.classes.text.white} mb-2`}>
                  {resetProgress.status === 'done' ? 'Draft reset complete!' : 'Resetting draft...'}
                </p>
                <div className="w-full bg-gray-700 rounded-full h-2 mb-2">
                  <div
                    className="bg-yellow-500 h-2 rounded-full transition-all"
                    style={{ width: `${resetProgress.total > 0 ? (resetProgress.completed / resetProgress.total) * 100 : 0}%` }}
                  />
                </div>
                <p className={`text-sm ${dynastyTheme.classes.text.neutralLight}`}>
                  {resetProgress.completed} / {resetProgress.total} picks undone
                  {resetProgress.errors > 0 && ` (${resetProgress.errors} errors)`}
                </p>
              </>
            )}
          </div>
        </div>
      )}

      {/* End Draft confirmation dialog */}
      {endDraftDialogOpen && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center" style={{ zIndex: 999999 }}>
          <div className={`${dynastyTheme.components.card.base} p-6 max-w-md mx-4`} onClick={(e) => e.stopPropagation()}>
            <h3 className={`${dynastyTheme.components.heading.h3} mb-4`}>
              End Draft Early?
            </h3>
            <p className={`${dynastyTheme.classes.text.white} mb-4`}>
              All completed picks will be finalized. The league will move to the <strong>Finalizing</strong> phase. This cannot be undone.
            </p>
            {endDraftError && (
              <p className="text-red-400 text-sm mb-4">{endDraftError}</p>
            )}
            <div className="flex gap-3">
              <button
                onClick={handleEndDraft}
                disabled={endDraftLoading}
                className="flex-1 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-medium disabled:opacity-50"
              >
                {endDraftLoading ? 'Ending...' : 'End Draft'}
              </button>
              <button
                onClick={() => { setEndDraftDialogOpen(false); setEndDraftError(null); }}
                disabled={endDraftLoading}
                className={`flex-1 ${dynastyTheme.components.button.secondary}`}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

    </>,
    document.body
  );

  // Inline mode: horizontal buttons in header
  if (inline) {
    return (
      <>
        <div className="flex items-center gap-2">
          {/* Start Draft */}
          {!draftHasStarted && (
            <button
              onClick={() => setStartDialogOpen(true)}
              disabled={actionLoading}
              className={`${dynastyTheme.components.button.primary} flex items-center gap-2 bg-green-600 hover:bg-green-700 px-4 py-2`}
            >
              <PlayCircle className="w-4 h-4" />
              Start Draft
            </button>
          )}

          {/* Pause/Resume */}
          {draftHasStarted && (
            isPaused ? (
              <button
                onClick={handleResume}
                disabled={actionLoading || !socket}
                className={`${dynastyTheme.components.button.primary} flex items-center gap-2 px-4 py-2`}
              >
                <Play className="w-4 h-4" />
                Resume
              </button>
            ) : (
              <button
                onClick={() => setPauseDialogOpen(true)}
                disabled={actionLoading || !socket}
                className={`${dynastyTheme.components.button.secondary} flex items-center gap-2 px-4 py-2`}
              >
                <Pause className="w-4 h-4" />
                Pause
              </button>
            )
          )}

          {/* Skip (only show when draft has started) */}
          {draftHasStarted && (
            <button
              onClick={handleSkip}
              disabled={!hasCurrentPick || isPaused || actionLoading || !socket}
              className={`${dynastyTheme.components.button.secondary} flex items-center gap-2 px-4 py-2`}
            >
              <SkipForward className="w-4 h-4" />
              Skip
            </button>
          )}

          {/* Undo (only show when draft has started) */}
          {draftHasStarted && (
            <button
              onClick={() => setUndoDialogOpen(true)}
              disabled={!hasPicksToUndo || actionLoading || !socket}
              className={`${dynastyTheme.components.button.secondary} flex items-center gap-2 px-4 py-2`}
            >
              <Undo2 className="w-4 h-4" />
              Undo
            </button>
          )}

          {/* Fill Remaining (only show when draft has started) */}
          {draftHasStarted && (
            <button
              onClick={() => setFillDialogOpen(true)}
              disabled={!hasCurrentPick || isPaused || isFilling || !socket}
              className={`${dynastyTheme.components.button.secondary} flex items-center gap-2 px-4 py-2 ${isFilling ? 'animate-pulse' : ''}`}
            >
              <FastForward className="w-4 h-4" />
              {isFilling ? `Filling (${fillProgress.completed}/${fillProgress.total})...` : 'Fill Remaining'}
            </button>
          )}

          {/* Reset Draft (only show when picks have been made) */}
          {draftHasStarted && hasPicksToUndo && (
            <button
              onClick={() => setResetDialogOpen(true)}
              disabled={isFilling || isResetting || !socket}
              className={`${dynastyTheme.components.button.secondary} flex items-center gap-2 px-4 py-2 text-red-400 border-red-500/50 hover:bg-red-900/20 ${isResetting ? 'animate-pulse' : ''}`}
            >
              <RotateCcw className="w-4 h-4" />
              {isResetting ? `Resetting (${resetProgress.completed}/${resetProgress.total})...` : 'Reset Draft'}
            </button>
          )}

          {/* End Draft (inline) */}
          {draftHasStarted && (
            <button
              onClick={() => setEndDraftDialogOpen(true)}
              disabled={isFilling || isResetting || endDraftLoading}
              className={`${dynastyTheme.components.button.secondary} flex items-center gap-2 px-4 py-2 text-orange-400 border-orange-500/50 hover:bg-orange-900/20`}
            >
              End Draft Early
            </button>
          )}
        </div>

        {renderDialogs()}
      </>
    );
  }

  // Card mode: vertical layout in sidebar
  return (
    <>
      <div className={`${dynastyTheme.components.card.base} p-4`}>
        <div className="flex items-center gap-2 mb-3">
          <AlertTriangle className={`w-5 h-5 ${dynastyTheme.classes.text.warning}`} />
          <h3 className={dynastyTheme.components.heading.h4}>
            Commissioner Controls
          </h3>
        </div>

        <div className="space-y-2">
          {/* Start Draft (only show if draft hasn't started) */}
          {!draftHasStarted && (
            <button
              onClick={() => setStartDialogOpen(true)}
              disabled={actionLoading}
              className={`${dynastyTheme.components.button.primary} w-full flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700`}
            >
              <PlayCircle className="w-5 h-5" />
              Start Draft
            </button>
          )}

          {/* Pause/Resume (only show if draft has started) */}
          {draftHasStarted && (
            isPaused ? (
              <button
                onClick={handleResume}
                disabled={actionLoading || !socket}
                className={`${dynastyTheme.components.button.primary} w-full flex items-center justify-center gap-2`}
              >
                <Play className="w-4 h-4" />
                Resume Draft
              </button>
            ) : (
              <button
                onClick={() => setPauseDialogOpen(true)}
                disabled={actionLoading || !socket}
                className={`${dynastyTheme.components.button.secondary} w-full flex items-center justify-center gap-2`}
              >
                <Pause className="w-4 h-4" />
                Pause Draft
              </button>
            )
          )}

          {/* Skip */}
          <button
            onClick={handleSkip}
            disabled={!hasCurrentPick || isPaused || actionLoading || !socket}
            className={`${dynastyTheme.components.button.secondary} w-full flex items-center justify-center gap-2`}
          >
            <SkipForward className="w-4 h-4" />
            Skip Current Pick
          </button>

          {/* Undo */}
          <button
            onClick={() => setUndoDialogOpen(true)}
            disabled={!hasPicksToUndo || actionLoading || !socket}
            className={`${dynastyTheme.components.button.secondary} w-full flex items-center justify-center gap-2`}
          >
            <Undo2 className="w-4 h-4" />
            Undo Last Pick
          </button>

          {/* Fill Remaining */}
          <button
            onClick={() => setFillDialogOpen(true)}
            disabled={!hasCurrentPick || isPaused || isFilling || !socket}
            className={`${dynastyTheme.components.button.secondary} w-full flex items-center justify-center gap-2 ${isFilling ? 'animate-pulse' : ''}`}
          >
            <FastForward className="w-4 h-4" />
            {isFilling ? `Filling (${fillProgress.completed}/${fillProgress.total})...` : 'Fill Remaining'}
          </button>

          {/* Reset Draft */}
          {hasPicksToUndo && (
            <button
              onClick={() => setResetDialogOpen(true)}
              disabled={isFilling || isResetting || !socket}
              className={`${dynastyTheme.components.button.secondary} w-full flex items-center justify-center gap-2 text-red-400 border-red-500/50 hover:bg-red-900/20 ${isResetting ? 'animate-pulse' : ''}`}
            >
              <RotateCcw className="w-4 h-4" />
              {isResetting ? `Resetting (${resetProgress.completed}/${resetProgress.total})...` : 'Reset Draft'}
            </button>
          )}

          {/* End Draft (commissioner only) */}
          {draftHasStarted && (
            <button
              onClick={() => setEndDraftDialogOpen(true)}
              disabled={isFilling || isResetting || endDraftLoading}
              className={`${dynastyTheme.components.button.secondary} w-full flex items-center justify-center gap-2 text-orange-400 border-orange-500/50 hover:bg-orange-900/20`}
            >
              End Draft Early
            </button>
          )}
        </div>
      </div>

      {renderDialogs()}
    </>
  );
};

export default DraftControls;
